import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight, Check, X, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PRICING, PRICING_COMPARISON, FAQ, BRAND } from "@/data/content";
import HonestyBlock from "@/components/sections/HonestyBlock";
import { track } from "@/lib/analytics";

function MonetizeAddonBlock({ annual }) {
  const { user } = useAuth();
  const [checkout, setCheckout] = useState(null);

  useEffect(() => {
    api.get("/monetize/checkout")
      .then((r) => setCheckout(r.data))
      .catch(() => setCheckout({ monthly: "", annual: "", monthly_price_cents: 4900, annual_price_cents: 47000 }));
  }, []);

  const baseUrl = annual ? checkout?.annual : checkout?.monthly;
  // If the user is signed in, attach client_reference_id + prefilled_email
  // so the Stripe webhook can grant Hub access instantly on payment.
  const url = (() => {
    if (!baseUrl) return "";
    try {
      const u = new URL(baseUrl);
      if (user?.user_id) u.searchParams.set("client_reference_id", user.user_id);
      if (user?.email) u.searchParams.set("prefilled_email", user.email);
      return u.toString();
    } catch {
      return baseUrl;
    }
  })();

  const priceLabel = annual ? "$39" : "$49";
  const cadence = annual ? "/mo · billed annually" : "/mo";
  const annualNote = annual ? "Save 20% - $470 / year" : "or $470/yr (save 20%)";

  const handleClick = (e) => {
    track("Monetize Addon Clicked", { annual });
    if (url) return; // let the anchor navigate to Stripe
    e.preventDefault();
    window.location.href = "/contact?topic=monetize-hub";
  };

  return (
    <div
      data-testid="pricing-monetize-addon"
      className="relative overflow-hidden border border-gold/40 bg-gradient-to-br from-gold/[0.08] via-transparent to-transparent p-8 sm:p-12"
    >
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
        <div className="lg:col-span-8">
          <div className="flex items-center gap-3 flex-wrap">
            <p className="overline">New · Premium add-on</p>
            <span className="text-[10px] uppercase tracking-[0.22em] text-emerald-300 border border-emerald-400/40 px-2 py-0.5">
              Included with Growth
            </span>
          </div>
          <h3 className="font-serif text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.05] mt-4">
            <span className="text-aurora-static">GoldKey Monetization Hub</span>
            <br />
            <span className="italic text-neutral-600 text-2xl sm:text-3xl">Turn your audience into revenue.</span>
          </h3>
          <p className="text-neutral-600 mt-5 leading-relaxed max-w-2xl">
            A Pillar.io-grade creator commerce layer that plugs into every GoldKey plan.
            Sell downloads, courses, memberships and 1:1 bookings from a single link-in-bio
            page. AI drafts each product in seconds. Templates for medspas, agencies and coaches
            come pre-loaded.
          </p>
          <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 text-sm text-neutral-700 max-w-2xl">
            {[
              "One-click link-in-bio storefront",
              "Digital downloads + ebooks + templates",
              "Online courses + recurring memberships",
              "Calendar bookings for 1:1 or group",
              "AI Product Generator (sentence → full product)",
              "Affiliate tracking + payouts",
              "Orders, customers, analytics dashboard",
              "Pulls from Content Studio + Growth Engine",
            ].map((f) => (
              <li key={f} className="flex gap-2 items-start">
                <Check size={13} className="flex-none mt-1 text-gold" />
                <span>{f}</span>
              </li>
            ))}
          </ul>
          <p className="text-xs text-neutral-500 mt-6 max-w-2xl">
            Compares favorably to Pillar Elite ($49), Stan Pro ($79), Podia Shaker ($89) and
            Kajabi Kickstarter ($149). Built into your Studio - no separate account.
          </p>
        </div>
        <div className="lg:col-span-4 lg:pl-6 lg:border-l border-neutral-200">
          <p className="overline">{annual ? "Annual - best value" : "Add on to any plan"}</p>
          <div className="flex items-baseline gap-2 mt-3" data-testid="monetize-price">
            <span className="font-serif text-5xl sm:text-6xl leading-none text-aurora-static">+{priceLabel}</span>
            <span className="text-xs uppercase tracking-[0.18em] text-neutral-600">{cadence}</span>
          </div>
          <p className="text-xs text-neutral-500 mt-3 leading-relaxed">{annualNote}</p>
          <p className="text-xs text-neutral-500 mt-1.5 leading-relaxed">
            Enable in one click. Cancel anytime.
          </p>
          <a
            href={url || "/contact?topic=monetize-hub"}
            target={url ? "_blank" : "_self"}
            rel="noreferrer"
            onClick={handleClick}
            data-testid="pricing-monetize-cta"
            className="btn-aurora mt-6 w-full justify-center"
          >
            <Sparkles size={13} /> Enable the Hub
          </a>
          {!url && checkout !== null && (
            <p className="text-[10px] text-neutral-400 mt-3 text-center leading-relaxed">
              Stripe checkout activating soon - we&apos;ll route you to our team for early access.
            </p>
          )}
          <Link
            to="/dashboard/monetize"
            data-testid="pricing-monetize-preview"
            className="mt-3 block text-center text-xs uppercase tracking-[0.18em] text-gold hover:text-gold"
          >
            Preview inside Studio →
          </Link>
        </div>
      </div>
    </div>
  );
}

function TierCta({ tier }) {
  const onClick = () =>
    track("Pricing Tier Clicked", {
      tier: tier.id,
      destination: tier.id === "enterprise" ? "calendly" : "stripe",
    });

  const className = `mt-10 inline-flex items-center justify-center gap-2 px-5 py-3 text-xs uppercase tracking-[0.2em] transition ${
    tier.featured
      ? "btn-aurora !py-3 !px-5"
      : "border border-neutral-200 text-neutral-900 hover:border-neutral-300 hover:bg-neutral-100"
  }`;

  if (tier.id === "enterprise") {
    return (
      <a
        href={BRAND.calendly}
        target="_blank"
        rel="noreferrer"
        onClick={onClick}
        data-testid={`pricing-cta-${tier.id}`}
        className={className}
      >
        {tier.cta} <ArrowUpRight size={14} />
      </a>
    );
  }
  if (tier.href) {
    return (
      <a
        href={tier.href}
        target="_blank"
        rel="noreferrer"
        onClick={onClick}
        data-testid={`pricing-cta-${tier.id}`}
        className={className}
      >
        {tier.cta} <ArrowUpRight size={14} />
      </a>
    );
  }
  return null;
}

function ComparisonCell({ value }) {
  if (value === true)
    return <Check size={16} className="text-gold mx-auto" data-testid="cmp-yes" />;
  if (value === false || value == null)
    return <X size={14} className="text-neutral-400 mx-auto" data-testid="cmp-no" />;
  return <span className="text-sm text-neutral-600">{value}</span>;
}

function computeCadenceLabel(tier, annual) {
  if (tier.id === "enterprise") return tier.cadence;
  if (annual) return "per month · billed yearly";
  return tier.cadence;
}

export default function PricingPage() {
  const [annual, setAnnual] = useState(false);

  return (
    <div data-testid="pricing-page" className="pt-32 sm:pt-40">
      <section className="px-5 sm:px-6 lg:px-12 pb-12 sm:pb-16">
        <div className="max-w-[1400px] mx-auto">
          <p className="overline">Pricing</p>
          <h1 className="font-serif text-5xl sm:text-6xl md:text-7xl lg:text-[8rem] leading-[0.95] tracking-tight mt-5 sm:mt-6 max-w-[14ch]">
            Simple.
            <br />
            <span className="italic text-aurora">Honest.</span>
          </h1>
          <p className="text-base sm:text-lg text-neutral-700 max-w-2xl mt-8 sm:mt-10 leading-relaxed">
            Two plans, plus an Enterprise option for teams. Monthly billing
            via Stripe. Annual saves 20%. Cancel any time - and every plan
            lets you export everything in your Vault the day you ask.
          </p>

          <div className="mt-8 sm:mt-10 inline-flex items-center gap-1 border border-neutral-200 p-1 rounded-sm backdrop-blur-md bg-white">
            <button
              type="button"
              onClick={() => setAnnual(false)}
              data-testid="billing-toggle-monthly"
              className={`px-4 py-2 text-xs uppercase tracking-[0.2em] transition rounded-sm ${
                !annual ? "bg-aurora text-neutral-900" : "text-neutral-700 hover:text-neutral-900"
              }`}
            >
              Monthly
            </button>
            <button
              type="button"
              onClick={() => setAnnual(true)}
              data-testid="billing-toggle-annual"
              className={`px-4 py-2 text-xs uppercase tracking-[0.2em] transition flex items-center gap-2 rounded-sm ${
                annual ? "bg-aurora text-neutral-900" : "text-neutral-700 hover:text-neutral-900"
              }`}
            >
              Annual <span className="text-[10px] opacity-80 not-italic">· save 20%</span>
            </button>
          </div>
        </div>
      </section>

      {/* Tier cards */}
      <section className="px-5 sm:px-6 lg:px-12 py-10 sm:py-16">
        <div className="max-w-[1400px] mx-auto grid grid-cols-1 md:grid-cols-3 gap-6">
          {PRICING.map((tier, i) => {
            const monthly = tier.price;
            const cadenceLabel = computeCadenceLabel(tier, annual);
            const numeric = parseFloat((monthly || "").replace(/[^0-9.]/g, ""));
            const display = annual && !Number.isNaN(numeric)
              ? `$${Math.round(numeric * 0.8)}`
              : monthly;
            return (
              <motion.div
                key={tier.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.7, delay: i * 0.08 }}
                data-testid={`pricing-tier-${tier.id}`}
                className={`relative p-8 sm:p-10 flex flex-col rounded-sm ${
                  tier.featured
                    ? "filigree-border"
                    : "border border-neutral-200 bg-white backdrop-blur-md"
                }`}
              >
                {tier.featured && (
                  <>
                    <div aria-hidden className="absolute -inset-px rounded-sm overflow-hidden -z-10">
                      <div className="aurora-orb aurora-orb-magenta" style={{ width: 280, height: 280, top: -80, left: -40, opacity: 0.35 }} />
                      <div className="aurora-orb aurora-orb-azure" style={{ width: 280, height: 280, bottom: -80, right: -40, opacity: 0.3 }} />
                    </div>
                    <span
                      data-testid={`pricing-featured-${tier.id}`}
                      className="absolute -top-3 left-8 text-[10px] uppercase tracking-[0.25em] px-3 py-1 rounded-sm text-neutral-900"
                      style={{ background: "linear-gradient(135deg, #FFF0B3 0%, #FFD700 50%, #C77F00 100%)", color: "#0a0703" }}
                    >
                      Most loved
                    </span>
                  </>
                )}
                <h3 className="font-serif text-3xl sm:text-4xl">{tier.name}</h3>
                <p className="text-neutral-600 mt-3 text-sm leading-relaxed">
                  {tier.tagline}
                </p>
                <div className="mt-6 mb-8 pb-8 border-b border-neutral-200 flex items-baseline gap-2">
                  <span className={`font-serif text-5xl sm:text-6xl leading-none ${tier.featured ? "text-aurora-static" : "text-neutral-900"}`}>
                    {display}
                  </span>
                  <span className="text-xs uppercase tracking-[0.18em] text-neutral-600">
                    {cadenceLabel}
                  </span>
                </div>
                <ul className="space-y-3 flex-1">
                  {tier.features.map((f) => (
                    <li
                      key={f}
                      className="text-sm text-neutral-600 flex gap-3 items-start"
                    >
                      <Check size={14} className={`flex-none mt-1 ${tier.featured ? "text-gold" : "text-neutral-700"}`} />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <TierCta tier={tier} />
              </motion.div>
            );
          })}
        </div>
        <p className="text-center text-xs text-neutral-500 mt-8 max-w-2xl mx-auto leading-relaxed">
          Monthly billing via Stripe · cancel or downgrade anytime · annual
          contracts save 20% · your Vault is exportable on every plan.
        </p>

        {/* Monetization Hub add-on */}
        <div className="max-w-[1400px] mx-auto mt-16 sm:mt-20 px-2">
          <MonetizeAddonBlock annual={annual} />
        </div>
      </section>

      {/* What you get - comparison table */}
      <section
        data-testid="pricing-compare"
        className="px-5 sm:px-6 lg:px-12 py-16 sm:py-24 border-t border-neutral-200"
      >
        <div className="max-w-[1400px] mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-10 sm:mb-14">
            <div className="md:col-span-5">
              <p className="overline">What you get</p>
              <h2 className="font-serif text-4xl sm:text-5xl md:text-6xl tracking-tight leading-[1.05] mt-5">
                Compare the
                <br />
                <span className="italic text-aurora">three plans.</span>
              </h2>
            </div>
            <div className="md:col-span-6 md:col-start-7 flex items-end">
              <p className="text-neutral-700 leading-relaxed text-base sm:text-lg">
                Beginner-friendly side-by-side of what&apos;s included. Every plan
                ships with exportable data and real support
                from a real person.
              </p>
            </div>
          </div>

          <div className="overflow-x-auto border border-neutral-200">
            <table className="w-full min-w-[640px] text-left">
              <thead className="bg-white border-b border-neutral-200">
                <tr>
                  <th className="text-[10px] uppercase tracking-[0.22em] text-neutral-600 px-4 sm:px-6 py-4 font-normal">Feature</th>
                  <th className="text-[10px] uppercase tracking-[0.22em] text-neutral-600 px-4 sm:px-6 py-4 font-normal text-center">Starter · $49</th>
                  <th className="text-[10px] uppercase tracking-[0.22em] text-gold px-4 sm:px-6 py-4 font-normal text-center">Growth · $199</th>
                  <th className="text-[10px] uppercase tracking-[0.22em] text-neutral-600 px-4 sm:px-6 py-4 font-normal text-center">Enterprise</th>
                </tr>
              </thead>
              <tbody>
                {PRICING_COMPARISON.map((row, i) => (
                  <tr
                    key={row.feature}
                    data-testid={`pricing-row-${i}`}
                    className="border-b border-neutral-200/70 last:border-b-0 hover:bg-white"
                  >
                    <td className="px-4 sm:px-6 py-4 text-sm text-neutral-600">{row.feature}</td>
                    <td className="px-4 sm:px-6 py-4 text-center"><ComparisonCell value={row.starter} /></td>
                    <td className="px-4 sm:px-6 py-4 text-center bg-gold/[0.02]"><ComparisonCell value={row.growth} /></td>
                    <td className="px-4 sm:px-6 py-4 text-center"><ComparisonCell value={row.enterprise} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Honesty block */}
      <HonestyBlock />

      {/* FAQ */}
      <section
        data-testid="pricing-faq"
        className="px-5 sm:px-6 lg:px-12 py-16 sm:py-24 border-t border-neutral-200"
      >
        <div className="max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-4">
            <p className="overline">Questions, answered</p>
            <h2 className="font-serif text-4xl sm:text-5xl md:text-6xl tracking-tight leading-[1.05] mt-5 sm:mt-6">
              The <span className="italic text-aurora">fine print,</span>
              <br />
              up front.
            </h2>
          </div>
          <div className="lg:col-span-7 lg:col-start-6">
            <FaqAccordion />
          </div>
        </div>
      </section>
    </div>
  );
}

function FaqAccordion() {
  const [open, setOpen] = useState(0);
  return (
    <div className="border-t border-neutral-200">
      {FAQ.map((f, i) => (
        <div
          key={f.q}
          data-testid={`faq-${i}`}
          className="border-b border-neutral-200"
        >
          <button
            onClick={() => {
              const next = open === i ? -1 : i;
              setOpen(next);
              if (next === i) track("FAQ Opened", { question: f.q });
            }}
            className="w-full flex items-start justify-between gap-6 py-5 sm:py-6 text-left group"
          >
            <span className="font-serif text-lg sm:text-xl text-neutral-900 group-hover:text-gold transition">
              {f.q}
            </span>
            <span
              className={`text-gold transition-transform mt-1 ${
                open === i ? "rotate-45" : ""
              }`}
            >
              +
            </span>
          </button>
          {open === i && (
            <p className="pb-6 text-neutral-700 leading-relaxed text-sm sm:text-base">
              {f.a}
            </p>
          )}
        </div>
      ))}
    </div>
  );
}
